import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Bell, AlertTriangle, CheckCircle, Clock, Settings, Eye,
  AlertCircle, XCircle, Pause, Check, Plus
} from 'lucide-react';
import { LoadingState, useToast } from '../ui';
import {
  getAlertsDashboardOverview,
  getAlertsTrend,
  getAlertRules,
  getAlertInstances,
  acknowledgeAlert,
  resolveAlert,
  snoozeAlert
} from '../../services/analyticsService';

const AlertsNotificationsTab = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['alerts-dashboard-overview'],
    queryFn: getAlertsDashboardOverview,
    staleTime: 30000
  });

  const { data: trend, isLoading: trendLoading } = useQuery({
    queryKey: ['alerts-trend'],
    queryFn: () => getAlertsTrend({ days: 7 }),
    staleTime: 60000
  });

  const { data: rules, isLoading: rulesLoading } = useQuery({
    queryKey: ['alert-rules'],
    queryFn: getAlertRules,
    staleTime: 60000
  });

  const { data: instances, isLoading: instancesLoading } = useQuery({
    queryKey: ['alert-instances'],
    queryFn: () => getAlertInstances({ status: 'ACTIVE' }),
    staleTime: 30000
  });

  // Mutations
  const acknowledgeMutation = useMutation({
    mutationFn: acknowledgeAlert,
    onSuccess: () => {
      queryClient.invalidateQueries(['alert-instances']);
      queryClient.invalidateQueries(['alerts-dashboard-overview']);
      success('Alert acknowledged');
    },
    onError: () => showError('Failed to acknowledge alert')
  });

  const resolveMutation = useMutation({
    mutationFn: (id) => resolveAlert(id, 'Resolved via dashboard'),
    onSuccess: () => {
      queryClient.invalidateQueries(['alert-instances']);
      queryClient.invalidateQueries(['alerts-dashboard-overview']);
      success('Alert resolved');
    },
    onError: () => showError('Failed to resolve alert')
  });

  const snoozeMutation = useMutation({
    mutationFn: (id) => snoozeAlert(id, 30),
    onSuccess: () => {
      queryClient.invalidateQueries(['alert-instances']);
      queryClient.invalidateQueries(['alerts-dashboard-overview']);
      success('Alert snoozed for 30 minutes');
    },
    onError: () => showError('Failed to snooze alert')
  });

  if (overviewLoading) {
    return <LoadingState message="Loading alerts dashboard..." />;
  }

  const statusCounts = overview?.status_counts || {};
  const severityCounts = overview?.severity_counts || {};
  const recentAlerts = overview?.recent_alerts || [];
  const rulesSummary = overview?.rules_summary || {};
  const trendData = trend?.trend_data || [];
  const activeAlerts = Array.isArray(instances) ? instances : [];

  // Pie chart data for status
  const statusPieData = [
    { name: 'Active', value: statusCounts.active || 0, color: '#EF4444' },
    { name: 'Acknowledged', value: statusCounts.acknowledged || 0, color: '#F59E0B' },
    { name: 'Snoozed', value: statusCounts.snoozed || 0, color: '#6B7280' },
    { name: 'Resolved', value: statusCounts.resolved || 0, color: '#10B981' }
  ].filter(d => d.value > 0);

  // Severity colors
  const severityColors = {
    CRITICAL: '#DC2626',
    HIGH: '#F59E0B',
    MEDIUM: '#3B82F6',
    LOW: '#6B7280'
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      CRITICAL: 'bg-red-100 text-red-800 border-red-300',
      HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
      MEDIUM: 'bg-blue-100 text-blue-800 border-blue-300',
      LOW: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return styles[severity] || styles.LOW;
  };

  const getStatusBadge = (status) => {
    const styles = {
      ACTIVE: 'bg-red-100 text-red-800',
      ACKNOWLEDGED: 'bg-yellow-100 text-yellow-800',
      SNOOZED: 'bg-gray-100 text-gray-800',
      RESOLVED: 'bg-green-100 text-green-800',
      ESCALATED: 'bg-purple-100 text-purple-800'
    };
    return styles[status] || styles.ACTIVE;
  };

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: Eye },
          { id: 'active', label: 'Active Alerts', icon: AlertTriangle },
          { id: 'rules', label: 'Alert Rules', icon: Settings }
        ].map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
              activeSection === section.id
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <section.icon className="h-4 w-4 mr-2" />
            {section.label}
          </button>
        ))}
      </div>

      {activeSection === 'overview' && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KPICard
              title="Active Alerts"
              value={statusCounts.active || 0}
              icon={AlertTriangle}
              color="red"
            />
            <KPICard
              title="Critical"
              value={severityCounts.critical || 0}
              icon={XCircle}
              color="red"
            />
            <KPICard
              title="Acknowledged"
              value={statusCounts.acknowledged || 0}
              icon={Eye}
              color="orange"
            />
            <KPICard
              title="Resolved (All Time)"
              value={statusCounts.resolved || 0}
              icon={CheckCircle}
              color="green"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Alert Status Distribution */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Alert Status Distribution</h3>
              {statusPieData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {statusPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-4 mt-4">
                    {statusPieData.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="text-sm text-gray-600">{entry.name}: {entry.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <div className="text-center">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                    <p>No alerts to display</p>
                  </div>
                </div>
              )}
            </div>

            {/* Alert Trend */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Alert Trend (Last 7 Days)</h3>
              {trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trendData.map(d => ({
                    ...d,
                    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={2} name="Total" />
                    <Line type="monotone" dataKey="critical" stroke="#DC2626" strokeWidth={2} name="Critical" />
                    <Line type="monotone" dataKey="resolved" stroke="#10B981" strokeWidth={2} name="Resolved" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <p>No trend data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Alerts */}
          {recentAlerts.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Recent Alerts (Last 24 Hours)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alert</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {recentAlerts.map((alert, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{alert.title}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">{alert.message}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{alert.alert_type}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getSeverityBadge(alert.severity)}`}>
                            {alert.severity}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(alert.status)}`}>
                            {alert.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(alert.triggered_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {activeSection === 'active' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Active Alerts ({activeAlerts.length})</h3>
          </div>

          {activeAlerts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alert</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Triggered</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {activeAlerts.map((alert) => (
                    <tr key={alert.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{alert.alert_rule?.name || 'Unknown'}</div>
                        <div className="text-sm text-gray-500 truncate max-w-md">{alert.trigger_data?.message || alert.metadata?.message || ''}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getSeverityBadge(alert.alert_rule?.severity)}`}>
                          {alert.alert_rule?.severity || 'MEDIUM'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(alert.triggered_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => acknowledgeMutation.mutate(alert.id)}
                            disabled={acknowledgeMutation.isPending}
                            className="inline-flex items-center px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                            title="Acknowledge"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Ack
                          </button>
                          <button
                            onClick={() => snoozeMutation.mutate(alert.id)}
                            disabled={snoozeMutation.isPending}
                            className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            title="Snooze 30min"
                          >
                            <Pause className="h-3 w-3 mr-1" />
                            Snooze
                          </button>
                          <button
                            onClick={() => resolveMutation.mutate(alert.id)}
                            disabled={resolveMutation.isPending}
                            className="inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                            title="Resolve"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Resolve
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">All Clear!</h3>
              <p className="text-gray-600">No active alerts at this time.</p>
            </div>
          )}
        </div>
      )}

      {activeSection === 'rules' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Alert Rules</h3>
              <p className="text-sm text-gray-600">
                {rulesSummary.active || 0} active / {rulesSummary.total || 0} total rules
              </p>
            </div>
            <button className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium">
              <Plus className="h-4 w-4 mr-2" />
              Create Rule
            </button>
          </div>

          {rulesLoading ? (
            <LoadingState message="Loading rules..." />
          ) : rules && rules.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rule Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rules.map((rule) => (
                    <tr key={rule.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                        <div className="text-sm text-gray-500">{rule.description}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{rule.alert_type}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getSeverityBadge(rule.severity)}`}>
                          {rule.severity}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                          rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <Settings className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Alert Rules</h3>
              <p className="text-gray-600">Create your first alert rule to start monitoring.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    orange: 'bg-orange-50 text-orange-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
};

export default AlertsNotificationsTab;
